// 판정 — 렌더된 파형이 성질 셋을 만족하는가
//
// 정본이 계약한 성질 셋 중 기계가 판정할 수 있는 몫을 여기서 낸다. **이 판정은
// 필요조건일 뿐이다.** 전부 초록이어도 사람이 "괴롭다"고 하면 불통이고, 그때 고치는
// 것은 성질이 아니라 프로파일이다. 소리의 거슬림은 스펙트럼 분포와 거칠기가 정하는
// 것이라 단일 음압 수치로는 재지지 않는다 — 그래서 RMS 임계 같은 숫자를 통과·불통
// 기준으로 두지 않는다.
//
// 항목은 일곱이고 성질과 이렇게 이어진다.
//
// - 셀 수 있을 것 → 표지 온셋들의 간격(grid)
// - 종점이 단 하나일 것 → 종점 온셋의 정확도(endpoint) · 종점이 표지와 두 축에서
//   다른가(distinct)
// - 오래 들어도 괴롭지 않을 것 → 듀티(duty) · 헤드룸(headroom) · 순음(sine)
// - 그리고 위 여섯의 전제 → 검출된 이벤트 개수(count)
//
// **전제가 무너지면 나머지를 재지 않고 그렇게 적는다.** 개수가 틀리면 어느 것이
// 표지이고 어느 것이 종점인지의 근거가 사라지고, 그 위에서 낸 간격·종점 판정은 숫자만
// 그럴듯하고 뜻이 없다. 측정 불가를 통과로 처리하지 않는 것이 이 저장소의 채점 규약이다.

import { detectEvents, soundingRatio, type DetectedEvent } from './onset.ts'
import { SIGNAL_PROFILE, type SignalProfile } from './profile.ts'

/** 정본이 못박은 시각 허용 오차. 표지 간격과 종점 온셋 둘 다에 걸린다. */
export const ONSET_TOLERANCE_SEC = 0.001

/**
 * 첨두율(최대 진폭 ÷ 실효값)이 이 범위 밖이면 사인이 아니라고 본다. 사인은 √2(약
 * 1.414), 사각파는 1.0, 톱니파·삼각파는 √3(약 1.732)이다.
 */
export const SINE_CREST_MIN = 1.3
export const SINE_CREST_MAX = 1.55

/**
 * 렌더된 최대 진폭이 풀스케일의 이 비율을 넘으면 헤드룸이 없다고 본다.
 *
 * **측정에서 나온 숫자가 아니다 — 규율이다.** 회수된 형태는 이 값이 1.0이라 헤드룸이
 * 0이었고, 그것이 실기기에서 무엇을 만들었는지는 이 저장소에 측정값이 없다. 0.9로
 * 둔 이유는 여유를 최소 10%는 남긴다는 것뿐이다.
 *
 * **2026-08-10 청취는 이 상한을 검증하지 않았다.** 사람이 들은 소리의 최대 진폭은 0.50
 * 이라 상한 근처에 가 본 적이 없다. 통과한 것은 그 값이지 이 문턱이 아니다.
 */
export const HEADROOM_CEILING = 0.9

/** 종점이 표지와 「다르다」고 인정하는 최소 폭. 두 축 각각에 걸린다. */
const PITCH_DIFFERENCE_MIN = 0.1
const LENGTH_DIFFERENCE_MIN = 0.2

export interface SignalCheck {
  readonly id: string
  readonly label: string
  readonly ok: boolean
  readonly detail: string
}

export interface SignalVerdict {
  readonly ok: boolean
  readonly checks: readonly SignalCheck[]
  readonly events: readonly DetectedEvent[]
}

const ms = (seconds: number): string => `${(seconds * 1000).toFixed(2)}밀리초`
const hz = (value: number): string => `${value.toFixed(0)} Hz`

function mean(values: number[]): number {
  if (values.length === 0) return 0
  return values.reduce((sum, v) => sum + v, 0) / values.length
}

/**
 * 렌더 버퍼를 판정한다. `startAtSec` 은 그 버퍼 안에서 개시(T=0)가 놓인 시각이다.
 *
 * 버퍼를 만드는 것은 이 함수의 일이 아니다. **실제 브라우저의 Web Audio 가 신호 모듈의
 * 예약을 그대로 렌더한 버퍼**가 들어와야 이 판정이 뜻을 갖는다 — 판정용으로 따로 만든
 * 파형을 판정하면 아무것도 검증되지 않는다.
 */
export function judgeSignal(input: {
  samples: Float32Array
  sampleRate: number
  startAtSec: number
  profile?: SignalProfile
}): SignalVerdict {
  const profile = input.profile ?? SIGNAL_PROFILE
  const { samples, sampleRate, startAtSec } = input

  const events = detectEvents(samples, { sampleRate })
  const expected = profile.events.length
  const markerCount = profile.events.filter((e) => e.role === 'marker').length

  const checks: SignalCheck[] = []

  const countOk = events.length === expected
  checks.push({
    id: 'count',
    label: '이벤트 개수',
    ok: countOk,
    detail: `검출 ${String(events.length)}개 · 프로파일 ${String(expected)}개`,
  })

  let peakAmplitude = 0
  for (const v of samples) {
    const abs = Math.abs(v)
    if (abs > peakAmplitude) peakAmplitude = abs
  }
  checks.push({
    id: 'headroom',
    label: '헤드룸 — 풀스케일에 닿지 않는가',
    ok: peakAmplitude < HEADROOM_CEILING,
    detail: `최대 진폭 ${peakAmplitude.toFixed(3)} (상한 ${String(HEADROOM_CEILING)})`,
  })

  if (!countOk) {
    const gated = '전제(이벤트 개수)가 무너져 재지 않았다'
    for (const [id, label] of [
      ['grid', '표지 간격 — 셀 수 있는가'],
      ['endpoint', '종점 온셋 — 개시와 같은가'],
      ['distinct', '종점이 표지와 두 축에서 다른가'],
      ['duty', '듀티 — 구간이 소리로 얼마나 차 있는가'],
      ['sine', '순음 — 사인인가'],
    ] as const) {
      checks.push({ id, label, ok: false, detail: gated })
    }
    return { ok: false, checks, events }
  }

  const markers = events.slice(0, markerCount)
  const endpoint = events[events.length - 1]

  const gaps: number[] = []
  for (let i = 1; i < markers.length; i += 1) {
    gaps.push((markers[i]?.onsetSec ?? 0) - (markers[i - 1]?.onsetSec ?? 0))
  }
  const worstGap = gaps.reduce(
    (worst, gap) =>
      Math.abs(gap - profile.markerIntervalSec) > Math.abs(worst - profile.markerIntervalSec)
        ? gap
        : worst,
    profile.markerIntervalSec,
  )
  checks.push({
    id: 'grid',
    label: '표지 간격 — 셀 수 있는가',
    ok: gaps.every((gap) => Math.abs(gap - profile.markerIntervalSec) <= ONSET_TOLERANCE_SEC),
    detail:
      `가장 어긋난 간격 ${ms(worstGap)} ` +
      `(선언값 ${ms(profile.markerIntervalSec)} · 허용 ±${ms(ONSET_TOLERANCE_SEC)})`,
  })

  const endpointError = (endpoint?.onsetSec ?? 0) - startAtSec
  checks.push({
    id: 'endpoint',
    label: '종점 온셋 — 개시와 같은가',
    ok: Math.abs(endpointError) <= ONSET_TOLERANCE_SEC,
    detail: `개시 대비 ${endpointError >= 0 ? '+' : '−'}${ms(Math.abs(endpointError))} (허용 ±${ms(ONSET_TOLERANCE_SEC)})`,
  })

  const markerPitch = mean(markers.map((m) => m.frequencyHz))
  const markerLength = mean(markers.map((m) => m.durationSec))
  const pitchGap = markerPitch === 0 ? 0 : Math.abs((endpoint?.frequencyHz ?? 0) - markerPitch) / markerPitch
  const lengthGap =
    markerLength === 0 ? 0 : Math.abs((endpoint?.durationSec ?? 0) - markerLength) / markerLength
  checks.push({
    id: 'distinct',
    label: '종점이 표지와 두 축에서 다른가',
    ok: pitchGap > PITCH_DIFFERENCE_MIN && lengthGap > LENGTH_DIFFERENCE_MIN,
    detail:
      `음고 ${hz(markerPitch)} → ${hz(endpoint?.frequencyHz ?? 0)} · ` +
      `길이 ${ms(markerLength)} → ${ms(endpoint?.durationSec ?? 0)}`,
  })

  const windowFrom = markers[0]?.onsetSec ?? 0
  const windowTo = endpoint?.onsetSec ?? windowFrom
  const duty = soundingRatio(samples, { sampleRate, fromSec: windowFrom, toSec: windowTo })
  checks.push({
    id: 'duty',
    label: '듀티 — 구간이 소리로 얼마나 차 있는가',
    ok: duty <= profile.dutyCeiling,
    detail: `${(duty * 100).toFixed(1)}% (상한 ${(profile.dutyCeiling * 100).toFixed(0)}%)`,
  })

  const crests = events.map((e) => e.crestFactor)
  const worstCrest = crests.reduce(
    (worst, crest) => (Math.abs(crest - Math.SQRT2) > Math.abs(worst - Math.SQRT2) ? crest : worst),
    Math.SQRT2,
  )
  checks.push({
    id: 'sine',
    label: '순음 — 사인인가',
    ok: crests.every((crest) => crest >= SINE_CREST_MIN && crest <= SINE_CREST_MAX),
    detail: `가장 벗어난 첨두율 ${worstCrest.toFixed(3)} (사인은 ${Math.SQRT2.toFixed(3)})`,
  })

  return { ok: checks.every((c) => c.ok), checks, events }
}
